/**
 * Centre d'aide - Page de lecture pour les utilisateurs
 * Design System v2 (Volvo-inspired)
 *
 * Les utilisateurs peuvent uniquement LIRE les articles.
 * La création/édition est réservée aux SuperAdmins.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import {
  Search,
  BookOpen,
  FileText,
  ChevronRight,
  ArrowLeft,
  Clock,
  Tag,
  HelpCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { kbAPI } from '../services/kb.service';

type View = 'list' | 'article';

export function HelpCenterPage() {
  const [view, setView] = useState<View>('list');
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Charger les articles publiés uniquement
  const { data: articlesData, isLoading } = useQuery({
    queryKey: ['kb-articles', 'published', searchTerm, selectedCategory],
    queryFn: () => kbAPI.list({
      search: searchTerm || undefined,
      category: selectedCategory || undefined,
      status: 'PUBLISHED',
      page: 1,
      limit: 50,
    }),
  });

  // Charger les catégories
  const { data: categories } = useQuery({
    queryKey: ['kb-categories'],
    queryFn: () => kbAPI.getCategories(),
  });

  // Charger un article spécifique
  const { data: selectedArticle, isLoading: loadingArticle } = useQuery({
    queryKey: ['kb-article', selectedArticleId],
    queryFn: () => kbAPI.getById(selectedArticleId!),
    enabled: !!selectedArticleId && view === 'article',
  });

  const handleSelectArticle = (id: string) => {
    setSelectedArticleId(id);
    setView('article');
  };

  const handleBack = () => {
    setView('list');
    setSelectedArticleId(null);
  };

  const articles = articlesData?.data || [];

  // Vue Article
  if (view === 'article') {
    if (loadingArticle) {
      return (
        <div className="px-8 pt-8 pb-12">
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-12 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full mx-auto" />
            <p className="text-gray-600 mt-4">Chargement de l'article...</p>
          </div>
        </div>
      );
    }

    if (!selectedArticle) {
      return (
        <div className="px-8 pt-8 pb-12">
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-12 text-center">
            <p className="text-red-500 mb-4">Article non trouvé</p>
            <button
              onClick={handleBack}
              className="px-4 py-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-xl transition-all duration-300"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="px-8 pt-8 pb-12 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <button
            onClick={handleBack}
            className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-600
                       hover:bg-white hover:text-teal-600 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-100/50
                       active:scale-95 transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {selectedArticle.category && (
                <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-lg">
                  {typeof selectedArticle.category === 'string'
                    ? selectedArticle.category
                    : selectedArticle.category.name}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{selectedArticle.title}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Mis à jour le {format(new Date(selectedArticle.updatedAt), 'dd MMM yyyy', { locale: fr })}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-8">
          <div
            className="prose prose-gray max-w-none
                       prose-headings:text-gray-900 prose-headings:font-semibold
                       prose-p:text-gray-700 prose-p:leading-relaxed
                       prose-a:text-teal-600 prose-a:no-underline hover:prose-a:underline
                       prose-strong:text-gray-900
                       prose-ul:text-gray-700 prose-ol:text-gray-700
                       prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedArticle.content || '') }}
          />
        </div>

        {/* Tags */}
        {selectedArticle.tags && selectedArticle.tags.length > 0 && (
          <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-5">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <Tag className="w-4 h-4" />
              Tags
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedArticle.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100/80 text-gray-600 text-sm rounded-lg"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vue Liste
  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 flex items-center gap-3">
            <HelpCircle className="w-10 h-10 text-teal-600" />
            Centre d'aide
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            Trouvez des réponses à vos questions
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[250px]">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400
                                 group-focus-within:text-teal-500 transition-colors duration-300" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                className="w-full pl-10 pr-4 py-2.5 bg-white/40 border border-white/50 rounded-xl
                           focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                           hover:bg-white/60 hover:border-teal-200 transition-all duration-300"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Category Filter */}
          <select
            className="px-4 py-2.5 bg-white/40 border border-white/50 rounded-xl
                       focus:ring-2 focus:ring-teal-500 hover:bg-white/60 hover:border-teal-200
                       transition-all duration-300 cursor-pointer min-w-[180px]"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {categories?.map((cat: any) => (
              <option key={cat.id || cat.name} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Articles List */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full" />
            <p className="text-gray-600 mt-4">Chargement des articles...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-teal-600" />
            </div>
            <p className="text-gray-900 font-medium text-lg">Aucun article trouvé</p>
            <p className="text-gray-500 mt-1">
              {searchTerm || selectedCategory
                ? 'Essayez de modifier vos critères de recherche'
                : 'Les articles d\'aide seront bientôt disponibles'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/20">
            {articles.map((article: any) => (
              <div
                key={article.id}
                className="p-5 bg-white/10 hover:bg-white hover:shadow-lg hover:shadow-teal-100/30
                           cursor-pointer transition-all duration-300 group border-l-4 border-transparent
                           hover:border-l-teal-500"
                onClick={() => handleSelectArticle(article.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-teal-500" />
                      {article.category && (
                        <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-lg">
                          {article.category}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors duration-300 text-lg">
                      {article.title}
                    </h3>
                    {article.excerpt && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {article.excerpt}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Footer */}
      <div className="bg-teal-50/50 backdrop-blur-xl rounded-3xl border border-teal-100 p-6 text-center">
        <p className="text-teal-800 font-medium">Vous ne trouvez pas ce que vous cherchez ?</p>
        <p className="text-teal-600 text-sm mt-1">
          Contactez notre équipe support en créant un ticket depuis la section Support.
        </p>
      </div>
    </div>
  );
}
